import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ChevronRight, RefreshCw, X } from 'lucide-react';
import { api } from '../api/client';
import InstructionLessonDialog from './InstructionLessonDialog';

const titleForPhase = phase => phase === 'before'
  ? 'Review skills before you begin'
  : phase === 'after' ? 'Review your skills' : 'Lesson Help';

const readableArea = value => String(value || 'Focus skill')
  .replaceAll('_', ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase());

export default function InstructionAccess({
  phase,
  assessmentId,
  performanceId,
  itemNumber,
  gradeIndex,
  questionIndex,
  deliveryIndex,
  variant = 'inline',
  reviewOnly = false
}) {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availabilityStatus, setAvailabilityStatus] = useState('checking');
  const [phaseEnabled, setPhaseEnabled] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [studyExpanded, setStudyExpanded] = useState(false);
  const openingRef = useRef(false);
  const requestEpoch = useRef(0);
  const scopeRef = useRef('');
  scopeRef.current = `${phase}\u0000${assessmentId}`;
  const studyLessons = useMemo(() => {
    if (phase !== 'before') return available;
    const unique = new Map();
    for (const lesson of available) {
      const identity = `${lesson.area || ''}\u0000${String(lesson.objective || '').trim().toLocaleLowerCase('en-US')}`;
      if (!unique.has(identity)) unique.set(identity, lesson);
    }
    return [...unique.values()];
  }, [available, phase]);
  const reviewedCount = studyLessons.filter(lesson => lesson.status === 'reviewed').length;
  const nextStudyLesson = [...studyLessons].filter(lesson => lesson.status !== 'reviewed')
    .sort((a, b) => String(b.lastActivityAt || '').localeCompare(String(a.lastActivityAt || '')))[0] || studyLessons[0];
  const exactItemPosition = Number.isSafeInteger(Number(gradeIndex)) && Number(gradeIndex) >= 0
    && Number.isSafeInteger(Number(questionIndex)) && Number(questionIndex) >= 0
    ? { gradeIndex: Number(gradeIndex), questionIndex: Number(questionIndex) } : {};
  const deliveredItemPosition = Number.isSafeInteger(Number(deliveryIndex)) && Number(deliveryIndex) >= 0
    ? { deliveryIndex: Number(deliveryIndex) } : {};

  useEffect(() => {
    requestEpoch.current += 1;
    openingRef.current = false;
    if (!assessmentId || (phase !== 'before' && !performanceId)) {
      setAvailable([]);
      setPhaseEnabled(false);
      setAvailabilityStatus('unavailable');
      setLoading(false);
      return undefined;
    }
    let active = true;
    let retryTimer;
    let retries = 0;
    setAvailable([]);
    setSelected(null);
    setChooserOpen(false);
    setOpeningId(null);
    setError('');
    setAvailabilityStatus('checking');
    setStudyExpanded(false);
    setLoading(true);
    const load = () => api.instructionalLessons.get('student', {
      phase, assessmentId, performanceId, itemNumber, ...exactItemPosition, ...deliveredItemPosition
    }).then(result => {
      if (!active) return;
      const lessons = Array.isArray(result.lessons) ? result.lessons : [];
      const status = result.status || (lessons.length ? 'available' : 'objective_unmapped');
      setAvailable(lessons);
      setPhaseEnabled(result.enabled !== false);
      setAvailabilityStatus(status);
      if (phase === 'during' && performanceId
        && ['missing_lesson', 'mapping_review', 'objective_unmapped', 'item_unresolved'].includes(status)) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          if (active && document.visibilityState === 'visible') load();
        }, 12000);
      }
    }).catch(err => {
      if (!active) return;
      if (phase === 'after' && err.status === 409 && retries++ < 5) retryTimer = setTimeout(() => {
        retryTimer = null;
        load();
      }, 900);
      else {
        setAvailabilityStatus('error');
        setError(err.message || 'Lesson help could not be loaded.');
      }
    }).finally(() => { if (active) setLoading(false); });
    load();
    return () => {
      active = false;
      requestEpoch.current += 1;
      clearTimeout(retryTimer);
    };
  }, [phase, assessmentId, performanceId, itemNumber, gradeIndex, questionIndex, deliveryIndex, reloadKey, reviewOnly]);

  const recordEvent = (lesson, action) => api.instructionalLessons.post(`${lesson.id}/event`, {
    action, phase, assessmentId, performanceId, itemNumber, ...exactItemPosition, ...deliveredItemPosition
  }).then(() => true).catch(() => false); // A network interruption must not trap the learner.

  const openLesson = async (lesson, { switching = false } = {}) => {
    if (openingRef.current) return;
    openingRef.current = true;
    const epoch = requestEpoch.current;
    try {
      setError('');
      setOpeningId(lesson.id);
      const content = await api.instructionalLessons.get(`${lesson.id}/content`, {
        phase, assessmentId, performanceId, itemNumber, ...exactItemPosition, ...deliveredItemPosition
      });
      if (epoch !== requestEpoch.current) return;
      if (switching && selected) void recordEvent(selected, 'closed');
      setSelected({ ...lesson, ...content, phase, assessmentId,
        currentItemNumber: phase === 'during' ? String(itemNumber || '') : null });
      setChooserOpen(false);
      void recordEvent(lesson, 'opened').then(recorded => {
        if (recorded && phase === 'before' && scopeRef.current === `before\u0000${assessmentId}`) setAvailable(current => current.map(row => row.id === lesson.id && row.status !== 'reviewed'
          ? { ...row, status: 'in_progress', lastActivityAt: new Date().toISOString() } : row));
      });
    } catch (err) {
      if (epoch === requestEpoch.current) setError(err.message || 'This lesson is temporarily unavailable.');
    } finally {
      if (epoch === requestEpoch.current) {
        setOpeningId(null);
        openingRef.current = false;
      }
    }
  };

  const selectedStudyIndex = phase === 'before' && selected
    ? studyLessons.findIndex(lesson => lesson.id === selected.id && lesson.objective === selected.objective)
    : -1;
  const previousLesson = selectedStudyIndex > 0 ? studyLessons[selectedStudyIndex - 1] : null;
  const nextLesson = selectedStudyIndex >= 0 && selectedStudyIndex < studyLessons.length - 1
    ? studyLessons[selectedStudyIndex + 1] : null;

  const openChooser = () => {
    if (available.length === 1) void openLesson(available[0]);
    else setChooserOpen(true);
  };
  const closeLesson = () => {
    requestEpoch.current += 1;
    openingRef.current = false;
    setOpeningId(null);
    if (selected) void recordEvent(selected, 'closed');
    setSelected(null);
  };
  const completeLesson = () => {
    if (selected) void recordEvent(selected, 'completed').then(recorded => {
      if (recorded && phase === 'before' && scopeRef.current === `before\u0000${assessmentId}`) setAvailable(current => current.map(row => row.id === selected.id
        ? { ...row, status: 'reviewed', lastActivityAt: new Date().toISOString() } : row));
    });
    if (phase === 'before' && nextLesson) void openLesson(nextLesson, { switching: true });
    else closeLesson();
  };
  useEffect(()=>{const close=()=>{requestEpoch.current+=1;openingRef.current=false;setSelected(null);setChooserOpen(false);};window.addEventListener('lesson-monitor-external-open',close);return()=>window.removeEventListener('lesson-monitor-external-open',close);},[]);
  useEffect(()=>{const start=event=>{if(phase==='before'&&event.detail?.assessmentId===assessmentId){if(nextStudyLesson)void openLesson(nextStudyLesson);else setStudyExpanded(true);}};window.addEventListener('assessnow-study-lessons-open',start);return()=>window.removeEventListener('assessnow-study-lessons-open',start);},[phase,assessmentId,nextStudyLesson]);
  const retry = () => setReloadKey(value => value + 1);

  if (phaseEnabled === false && !loading) return variant === 'toolbar'
    ? <span hidden data-instruction-status="phase_disabled" /> : null;
  if (variant !== 'toolbar' && !available.length && !error && !loading) return reviewOnly ? <p className="review-empty-lessons" role="status">Lessons are not available yet. Your teacher can check the instructional gaps for this assessment.</p> : null;

  if (variant === 'toolbar') {
    return <>
      <div className="assessment-lesson-help" data-instruction-status={availabilityStatus} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flex: '0 0 auto', minWidth: 0 }}>
        <button type="button" onClick={openChooser} disabled={loading || openingId != null}
          aria-haspopup="dialog" aria-label={`${titleForPhase(phase)} for the current item`}
          style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
            padding: '9px 14px', borderRadius: 12, border: '1px solid #0f766e', background: '#e8f8f2',
            color: '#0f4f43', boxShadow: '0 3px 9px rgba(15,118,110,.14)', cursor: loading || openingId != null ? 'wait' : 'pointer',
            fontWeight: 850, fontSize: '.92rem', whiteSpace: 'nowrap' }}>
          <BookOpen size={19} aria-hidden="true" />
          <span>{loading ? 'Checking Lesson Help…' : openingId != null ? 'Opening lesson…' : 'Lesson Help'}</span>
          {available.length > 1 && <span aria-label={`${available.length} lessons`} style={{ minWidth: 22, height: 22, padding: '0 6px', borderRadius: 999,
            display: 'inline-grid', placeItems: 'center', background: '#0f766e', color: '#fff', fontSize: 12 }}>{available.length}</span>}
        </button>
        {error && <div role="alert" style={{ maxWidth: 290, fontSize: 12, lineHeight: 1.35, color: '#991b1b', textAlign: 'right' }}>
          Lesson help is temporarily unavailable.{' '}
          <button type="button" onClick={retry} style={{ border: 0, padding: 0, background: 'transparent', color: '#7f1d1d', textDecoration: 'underline', fontWeight: 800, cursor: 'pointer' }}>Try again</button>
        </div>}
      </div>

      {chooserOpen && <div role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setChooserOpen(false); }}
        style={{ position: 'fixed', inset: 0, zIndex: 4700, display: 'grid', placeItems: 'center', padding: 14, background: 'rgba(9,19,36,.74)' }}>
        <section role="dialog" aria-modal="true" aria-labelledby="lesson-help-title"
          style={{ width: 'min(680px,100%)', maxHeight: 'min(82vh,760px)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            borderRadius: 20, background: '#fff', color: '#172033', boxShadow: '0 28px 80px rgba(0,0,0,.34)' }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 16, padding: '20px 22px 16px', borderBottom: '1px solid #dbe6e2', background: 'linear-gradient(135deg,#effbf7,#f8fafc)' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f766e', fontSize: 12, fontWeight: 850, letterSpacing: '.08em', textTransform: 'uppercase' }}><BookOpen size={17} /> Current-item support</div>
              <h2 id="lesson-help-title" style={{ margin: '6px 0 3px', fontSize: 'clamp(1.3rem,3vw,1.75rem)' }}>Choose a lesson</h2>
              <p style={{ margin: 0, color: '#526173', lineHeight: 1.45 }}>These lessons match the objective of this question. Your assessment timer continues while a lesson is open.</p>
            </div>
            <button type="button" onClick={() => setChooserOpen(false)} aria-label="Close lesson chooser"
              style={{ width: 42, height: 42, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: 11, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' }}><X size={21} /></button>
          </header>
          <div style={{ overflowY: 'auto', padding: '16px 22px 22px', display: 'grid', gap: 10 }}>
            {!available.length && !loading && <div role="status" style={{ padding: '18px', borderRadius: 14, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', lineHeight: 1.55 }}>
              <strong style={{ display: 'block', color: '#1e293b', marginBottom: 5 }}>No matching lesson is available for this question yet.</strong>
              You can continue the assessment. Lesson Help will update automatically when an exact approved lesson is available.
              <div style={{ marginTop: 14 }}><button type="button" onClick={retry}
                style={{ minHeight: 42, padding: '8px 14px', borderRadius: 10, border: '1px solid #0f766e', background: '#fff', color: '#0f5f52', fontWeight: 800, cursor: 'pointer' }}><RefreshCw size={16} style={{ verticalAlign: 'middle', marginRight: 7 }} />Check again</button></div>
            </div>}
            {available.map(lesson => <button type="button" key={`${lesson.id}:${lesson.objective}`} onClick={() => void openLesson(lesson)} disabled={openingId != null}
              style={{ width: '100%', minWidth: 0, display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 14,
                padding: '15px 16px', textAlign: 'left', borderRadius: 14, border: '1px solid #b7d7cf', background: '#f4fcf9', color: '#173d35', cursor: openingId != null ? 'wait' : 'pointer' }}>
              <span style={{ minWidth: 0 }}>
                <strong style={{ display: 'block', fontSize: '1rem', overflowWrap: 'anywhere' }}>{lesson.title}</strong>
                <span style={{ display: 'block', marginTop: 4, fontSize: '.86rem', lineHeight: 1.4, color: '#45665e', overflowWrap: 'anywhere' }}>{lesson.objective}</span>
                <small style={{ display: 'block', marginTop: 5, color: '#60766f' }}>{readableArea(lesson.area)}</small>
              </span>
              <ChevronRight size={21} aria-hidden="true" />
            </button>)}
            {error && <div role="alert" style={{ padding: 10, borderRadius: 10, background: '#fef2f2', color: '#991b1b' }}>{error}</div>}
          </div>
        </section>
      </div>}
      {selected && <InstructionLessonDialog lesson={selected} onClose={closeLesson} onComplete={completeLesson} />}
    </>;
  }

  return <aside data-instruction-study={phase === 'before' ? 'entry' : undefined} style={{ border: '1px solid #8dcbb9', borderRadius: 17, background: 'linear-gradient(135deg,#ecfaf5,#f8fcff)', color: '#123d34', padding: 'clamp(14px,2vw,20px)', margin: '14px 0', boxShadow: '0 6px 20px rgba(15,118,110,.1)', minWidth: 0 }}>
    {loading && <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 750 }}><RefreshCw size={16} className="spin" /> Checking available lessons…</div>}
    {!!studyLessons.length && <>
      {phase === 'before' ? <>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <strong style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.05rem' }}><BookOpen size={21} aria-hidden="true" /> Study my lessons</strong>
            <div style={{ fontSize: 13, marginTop: 5, lineHeight: 1.45 }}>Explore {studyLessons.length} {studyLessons.length === 1 ? 'skill' : 'skills'} at your own pace. {reviewedCount > 0 ? `${reviewedCount} reviewed. ` : ''}{reviewOnly ? 'Assessment-taking is paused. Choose a skill to review.' : 'The assessment begins only when you press Start Assessment.'}</div>
          </div>
          {nextStudyLesson && <button type="button" onClick={() => void openLesson(nextStudyLesson)} disabled={openingId != null}
            style={{ minHeight: 46, padding: '10px 16px', borderRadius: 11, border: '1px solid #0c6654', background: '#fff', color: '#0b594a', fontWeight: 850, cursor: openingId != null ? 'wait' : 'pointer' }}>
            {reviewedCount === studyLessons.length ? 'Review lessons again' : studyLessons.some(lesson => lesson.status === 'in_progress') ? 'Continue studying' : 'Begin studying'}
          </button>}
          <button type="button" aria-expanded={studyExpanded} aria-controls="study-lesson-list" onClick={() => setStudyExpanded(value => !value)}
            style={{ minHeight: 46, padding: '10px 16px', borderRadius: 11, border: '1px solid #0c6654', background: '#0f766e', color: '#fff', fontWeight: 850, fontSize: '.95rem', cursor: 'pointer', boxShadow: '0 4px 10px rgba(15,118,110,.18)' }}>
            {studyExpanded ? 'Hide lesson list' : 'Explore lessons'}
          </button>
        </div>
        {studyExpanded && <div id="study-lesson-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))', gap: 10, marginTop: 15 }}>
          {studyLessons.map((lesson, index) => <button type="button" key={`${lesson.id}:${lesson.area}:${lesson.objective}`} onClick={() => void openLesson(lesson)} disabled={openingId != null}
            style={{ minWidth: 0, minHeight: 86, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, textAlign: 'left', border: '1px solid #b7d7cf', background: '#fff', color: '#173d35', padding: '13px 15px', borderRadius: 13, cursor: openingId != null ? 'wait' : 'pointer', boxShadow: '0 2px 8px rgba(16,64,52,.06)' }}>
            <span style={{ minWidth: 0 }}><small style={{ display: 'block', fontSize: 11, fontWeight: 850, color: '#0f766e', marginBottom: 4 }}>LESSON {index + 1} · {readableArea(lesson.area)} · {lesson.status === 'reviewed' ? 'Reviewed' : lesson.status === 'in_progress' ? 'In progress' : 'Not started'}</small>
              <strong style={{ display: 'block', fontSize: '.98rem', overflowWrap: 'anywhere' }}>{openingId === lesson.id ? 'Opening…' : lesson.title}</strong>
              <span style={{ display: 'block', fontSize: 12, color: '#47645b', marginTop: 4, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{lesson.objective}</span></span>
            <ChevronRight size={21} style={{ flex: '0 0 auto' }} aria-hidden="true" />
          </button>)}
        </div>}
      </> : <>
        <strong style={{ display: 'flex', alignItems: 'center', gap: 7 }}><BookOpen size={18} />{titleForPhase(phase)}</strong>
        <div className="instruction-objective-cards" style={{ display: 'grid', gridTemplateColumns:'minmax(0,1fr)', gap: 8, marginTop: 9 }}>{studyLessons.map(lesson => <button type="button" key={`${lesson.id}:${lesson.objective}`} onClick={() => void openLesson(lesson)} disabled={openingId != null}
          style={{ width:'100%',minWidth:0,minHeight:64,textAlign:'left',overflowWrap:'anywhere',lineHeight:1.45,display:'flex',alignItems:'center', border: '1px solid #27856e', background: '#fff', color: '#17473e', padding: '9px 12px', borderRadius: 9, fontWeight: 750, cursor: openingId != null ? 'wait' : 'pointer' }}>{openingId === lesson.id ? 'Opening…' : lesson.title}</button>)}</div>
      </>}
    </>}
    {error && <p role="alert" style={{ color: '#9f1d1d', marginBottom: 0 }}>Instruction is temporarily unavailable: {error} <button type="button" onClick={retry} style={{ border: 0, background: 'transparent', color: 'inherit', textDecoration: 'underline', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}><RefreshCw size={13} /> Retry</button></p>}
    {selected && <InstructionLessonDialog key={`${selected.id}:${selected.objective}`} lesson={selected} onClose={closeLesson} onComplete={completeLesson}
      onPrevious={previousLesson ? () => void openLesson(previousLesson, { switching: true }) : undefined}
      onNext={nextLesson ? () => void openLesson(nextLesson, { switching: true }) : undefined}
      position={selectedStudyIndex >= 0 ? selectedStudyIndex + 1 : undefined} total={phase === 'before' ? studyLessons.length : undefined}
      navigating={openingId != null} navigationError={error} />}
  </aside>;
}
